import { createRecipe } from '../__generated/implementation.js';
import { getRectangularShapeAnchorPointRecipes, getRoundShapeAnchorPointRecipes } from './plate.presets.js';
const holeAnchorPointOffset = 10000;
const recipeMetadata = {
    BoltHole: {
        title: 'Bolt Hole',
        description: 'Single bolt hole with adjustable diameter.',
        thumbnailUrl: '/thumbnails/circular-hole.png'
    },
    CornerBoltHoles: {
        title: 'Bolt Hole (All Corners)',
        description: 'Bolt hole for all corners of the plate.',
        thumbnailUrl: '/thumbnails/circular-hole.png'
    },
    KickPlateBoltHoles: {
        title: 'Bolt Holes',
        description: 'Bolt holes for horizontal edges.',
        thumbnailUrl: '/thumbnails/circular-hole.png'
    },
    OvalCylinderHole: {
        title: 'Oval Cylinder Hole',
        description: '',
        thumbnailUrl: '/thumbnails/oval-cylinder-hole.png'
    },
    RimCylinderHole: {
        title: 'Rim Cylinder Hole',
        description: 'Fixed diameter',
        thumbnailUrl: '/thumbnails/rim-cylinder-hole.png'
    },
    EuroCylinderHoleAu: {
        title: 'Euro Cylinder Hole',
        description: 'Australian orientation',
        thumbnailUrl: '/thumbnails/euro-cylinder-hole-au.png'
    },
    EuroCylinderHoleEu: {
        title: 'Euro Cylinder Hole',
        description: 'European orientation',
        thumbnailUrl: '/thumbnails/euro-cylinder-hole-eu.png'
    },
    CamlockHole: {
        title: 'Camlock Hole',
        description: '',
        thumbnailUrl: '/thumbnails/camlock-hole.png'
    },
    PlainCylinderHole: {
        title: 'Cylinder Hole',
        description: 'Adjustable diameter',
        thumbnailUrl: '/thumbnails/circular-hole.png'
    },
    Hole54: {
        title: '54 mm Lockset Hole',
        description: 'Fixed diameter',
        thumbnailUrl: '/thumbnails/circular-hole.png'
    },
    PD7Hole: {
        title: 'PD7 Lockset Hole',
        description: '',
        thumbnailUrl: '/thumbnails/pd7.png'
    },
    PN6Hole: {
        title: 'PN6 Lockset Hole',
        description: '',
        thumbnailUrl: '/thumbnails/pn6.png'
    },
    DeadboltHole: {
        title: 'Deadbolt Hole',
        description: 'Fixed diameter',
        thumbnailUrl: '/thumbnails/circular-hole.png'
    },
    PlainLocksetHole: {
        title: 'Lockset Hole',
        description: 'Adjustable diameter',
        thumbnailUrl: '/thumbnails/circular-hole.png'
    },
    RectangularHole: {
        title: 'Rectangular Hole',
        description: 'Adjustable rectangular shape',
        thumbnailUrl: '/thumbnails/rectangular-hole.png'
    },
    SquareHole: {
        title: 'Square Hole',
        description: 'Adjustable square shape',
        thumbnailUrl: '/thumbnails/square-hole.png'
    },
    CountersinkHole: {
        title: 'Countersink Hole',
        thumbnailUrl: '/thumbnails/countersink-hole.png'
    },
    CornerCountersinkHoles: {
        title: 'Countersink Hole (All Corners)',
        thumbnailUrl: '/thumbnails/countersink-hole.png'
    },
    KickPlateCountersinkHoles: {
        title: 'Countersink Holes',
        thumbnailUrl: '/thumbnails/countersink-hole.png'
    }
};
const getCountersinkSubtitle = (topRadius, bottomRadius)=>{
    if (topRadius <= 0) {
        return `There's no countersink hole type available for currently selected plate.`;
    }
    return `\u00d8\u00a0${topRadius * 2}mm, 90\u00b0\u00a0countersink, \u00d8\u00a0${bottomRadius * 2}mm through`;
};
const getCountersinkRecipeMetadata = (type, topRadius, bottomRadius)=>({
        title: recipeMetadata[type].title,
        description: getCountersinkSubtitle(topRadius, bottomRadius),
        thumbnailUrl: recipeMetadata[type].thumbnailUrl
    });
export const generateBoltHoleRecipes = (maxSize, allowedHoleTypes)=>{
    const recipes = [];
    if (allowedHoleTypes.single) {
        recipes.push({
            ...recipeMetadata.BoltHole,
            recipes: [
                createRecipe({
                    id: 'BoltHole-1',
                    type: 'BoltHole',
                    radius: Math.min(2.5, maxSize.x / 2, maxSize.y / 2),
                    x: 0,
                    y: 0,
                    anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`
                }),
                ...getRoundShapeAnchorPointRecipes('BoltHole-1', holeAnchorPointOffset)
            ]
        });
    }
    if (allowedHoleTypes.corner) {
        recipes.push({
            ...recipeMetadata.CornerBoltHoles,
            recipes: [
                createRecipe({
                    id: 'CornerBoltHoles-1',
                    type: 'CornerBoltHoles',
                    radius: Math.min(2.5, maxSize.x / 2, maxSize.y / 2),
                    x: 0,
                    y: 0,
                    anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`
                }),
                ...getRoundShapeAnchorPointRecipes('CornerBoltHoles-1', holeAnchorPointOffset)
            ]
        });
    }
    if (allowedHoleTypes.kickPlate) {
        recipes.push({
            ...recipeMetadata.KickPlateBoltHoles,
            recipes: [
                createRecipe({
                    id: 'KickPlateBoltHoles-1',
                    type: 'KickPlateBoltHoles',
                    radius: Math.min(2.5, maxSize.x / 2, maxSize.y / 2),
                    x: 0,
                    y: 0,
                    anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`,
                    horizontalCount: -1,
                    verticalCount: -1
                }),
                ...getRoundShapeAnchorPointRecipes('KickPlateBoltHoles-1', holeAnchorPointOffset)
            ]
        });
    }
    return recipes;
};
export const generateCylinderHoleRecipes = (maxSize)=>[
        {
            ...recipeMetadata.OvalCylinderHole,
            recipes: [
                createRecipe({
                    id: 'OvalCylinderHole-1',
                    type: 'OvalCylinderHole',
                    width: 19,
                    height: 33,
                    x: 0,
                    y: 0,
                    anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`
                }),
                ...getRoundShapeAnchorPointRecipes('OvalCylinderHole-1', holeAnchorPointOffset)
            ]
        },
        // Circular hole of a fixed size
        {
            ...recipeMetadata.RimCylinderHole,
            recipes: [
                createRecipe({
                    id: 'RimCylinderHole-1',
                    type: 'RimCylinderHole',
                    radius: 15.25,
                    x: 0,
                    y: 0,
                    anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`
                }),
                ...getRoundShapeAnchorPointRecipes('RimCylinderHole-1', holeAnchorPointOffset)
            ]
        },
        {
            ...recipeMetadata.EuroCylinderHoleAu,
            recipes: [
                createRecipe({
                    id: 'EuroCylinderHoleAu-1',
                    type: 'EuroCylinderHoleAu',
                    topWidth: 10.7,
                    bottomWidth: 17.75,
                    height: 33.6,
                    x: 0,
                    y: 0,
                    anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`
                }),
                ...getRoundShapeAnchorPointRecipes('EuroCylinderHoleAu-1', holeAnchorPointOffset)
            ]
        },
        {
            ...recipeMetadata.EuroCylinderHoleEu,
            recipes: [
                createRecipe({
                    id: 'EuroCylinderHoleEu-1',
                    type: 'EuroCylinderHoleEu',
                    topWidth: 10.7,
                    bottomWidth: 17.75,
                    height: 33.6,
                    x: 0,
                    y: 0,
                    anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`
                }),
                ...getRoundShapeAnchorPointRecipes('EuroCylinderHoleEu-1', holeAnchorPointOffset)
            ]
        },
        {
            ...recipeMetadata.CamlockHole,
            recipes: [
                createRecipe({
                    id: 'CamlockHole-1',
                    type: 'CamlockHole',
                    width: 16.5,
                    height: 19.4,
                    x: 0,
                    y: 0,
                    anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`
                }),
                ...getRoundShapeAnchorPointRecipes('CamlockHole-1', holeAnchorPointOffset)
            ]
        },
        // Circular hole with variable size, default is equivalent to Rim
        {
            ...recipeMetadata.PlainCylinderHole,
            recipes: [
                createRecipe({
                    id: 'PlainCylinderHole-1',
                    type: 'PlainCylinderHole',
                    radius: Math.min(16, maxSize.x / 2, maxSize.y / 2),
                    x: 0,
                    y: 0,
                    anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`
                }),
                ...getRoundShapeAnchorPointRecipes('PlainCylinderHole-1', holeAnchorPointOffset)
            ]
        }
    ];
export const generateLocksetHoleRecipes = (maxSize)=>[
        // Circular hole with standard fixed size
        {
            ...recipeMetadata.Hole54,
            recipes: [
                createRecipe({
                    id: 'Hole54-1',
                    type: 'Hole54',
                    radius: 27,
                    x: 0,
                    y: 0,
                    anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`
                }),
                ...getRoundShapeAnchorPointRecipes('Hole54-1', holeAnchorPointOffset)
            ]
        },
        {
            ...recipeMetadata.PD7Hole,
            recipes: [
                createRecipe({
                    id: 'PD7Hole-1',
                    type: 'PD7Hole',
                    primaryRadius: 27,
                    verticalCenterDistance: 34.65,
                    verticalRadius: 3.5,
                    diagonalCenterDistance: 33,
                    diagonalRadius: 3,
                    horizontalCenterDistance: 29,
                    horizontalRadius: 2.5,
                    x: 0,
                    y: 0,
                    anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`
                }),
                ...getRoundShapeAnchorPointRecipes('PD7Hole-1', holeAnchorPointOffset)
            ]
        },
        {
            ...recipeMetadata.PN6Hole,
            recipes: [
                createRecipe({
                    id: 'PN6Hole-1',
                    type: 'PN6Hole',
                    width: 69,
                    height: 55,
                    sideRadius: 3.25,
                    joinRadius: 7,
                    x: 0,
                    y: 0,
                    anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`
                }),
                ...getRoundShapeAnchorPointRecipes('PN6Hole-1', holeAnchorPointOffset)
            ]
        },
        {
            ...recipeMetadata.DeadboltHole,
            recipes: [
                createRecipe({
                    id: 'DeadboltHole-1',
                    type: 'DeadboltHole',
                    radius: 27,
                    x: 0,
                    y: 0,
                    anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`
                }),
                ...getRoundShapeAnchorPointRecipes('DeadboltHole-1', holeAnchorPointOffset)
            ]
        },
        {
            ...recipeMetadata.PlainLocksetHole,
            recipes: [
                // Circular hole with variable size, default is 54mm
                createRecipe({
                    id: 'PlainLocksetHole-1',
                    type: 'PlainLocksetHole',
                    radius: Math.min(27, maxSize.x / 2, maxSize.y / 2),
                    x: 0,
                    y: 0,
                    anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`
                }),
                ...getRoundShapeAnchorPointRecipes('PlainLocksetHole-1', holeAnchorPointOffset)
            ]
        }
    ];
export const generateShapeHoleRecipes = (maxSize)=>[
        {
            ...recipeMetadata.RectangularHole,
            recipes: [
                createRecipe({
                    id: 'RectangularHole-1',
                    type: 'RectangularHole',
                    cornerRadius: 2,
                    width: Math.min(10, maxSize.x),
                    height: Math.min(20, maxSize.y),
                    x: 0,
                    y: 0,
                    anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`
                }),
                ...getRectangularShapeAnchorPointRecipes('RectangularHole-1', holeAnchorPointOffset)
            ]
        },
        {
            ...recipeMetadata.SquareHole,
            recipes: [
                createRecipe({
                    id: 'SquareHole-1',
                    type: 'SquareHole',
                    cornerRadius: 2,
                    sideLength: Math.min(10, maxSize.x, maxSize.y),
                    x: 0,
                    y: 0,
                    anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`
                }),
                ...getRectangularShapeAnchorPointRecipes('SquareHole-1', holeAnchorPointOffset)
            ]
        }
    ];
export const generateCountersinkHoleRecipes = (countersinks, thickness, allowedHoleTypes)=>{
    const plateCountersinks = countersinks.filter((c)=>c.plateThicknessMin <= thickness && c.plateThicknessMax >= thickness);
    if (plateCountersinks.length === 0) {
        return [
            {
                ...getCountersinkRecipeMetadata('CountersinkHole', 0, 0),
                recipes: [
                    createRecipe({
                        id: 'CountersinkHole-1',
                        type: 'CountersinkHole',
                        topRadius: 0,
                        bottomRadius: 0,
                        countersinkId: 'Countersink-invalid-1',
                        x: 0,
                        y: 0,
                        anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`
                    }),
                    ...getRoundShapeAnchorPointRecipes('CountersinkHole-1', holeAnchorPointOffset)
                ]
            }
        ];
    }
    const recipes = [];
    if (allowedHoleTypes.single) {
        recipes.push(...plateCountersinks.map(({ id, topRadius, bottomRadius })=>{
            return {
                ...getCountersinkRecipeMetadata('CountersinkHole', topRadius, bottomRadius),
                recipes: [
                    createRecipe({
                        id: 'CountersinkHole-1',
                        type: 'CountersinkHole',
                        topRadius,
                        bottomRadius,
                        countersinkId: id,
                        x: 0,
                        y: 0,
                        anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`
                    }),
                    ...getRoundShapeAnchorPointRecipes('CountersinkHole-1', holeAnchorPointOffset)
                ]
            };
        }));
    }
    if (allowedHoleTypes.corner) {
        recipes.push(...plateCountersinks.map(({ id, topRadius, bottomRadius })=>{
            return {
                ...getCountersinkRecipeMetadata('CornerCountersinkHoles', topRadius, bottomRadius),
                recipes: [
                    createRecipe({
                        id: 'CornerCountersinkHoles-1',
                        type: 'CornerCountersinkHoles',
                        topRadius,
                        bottomRadius,
                        countersinkId: id,
                        x: 0,
                        y: 0,
                        anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`
                    }),
                    ...getRoundShapeAnchorPointRecipes('CornerCountersinkHoles-1', holeAnchorPointOffset)
                ]
            };
        }));
    }
    if (allowedHoleTypes.kickPlate) {
        recipes.push(...plateCountersinks.map(({ id, topRadius, bottomRadius })=>{
            return {
                ...getCountersinkRecipeMetadata('KickPlateCountersinkHoles', topRadius, bottomRadius),
                recipes: [
                    createRecipe({
                        id: 'KickPlateCountersinkHoles-1',
                        type: 'KickPlateCountersinkHoles',
                        topRadius,
                        bottomRadius,
                        countersinkId: id,
                        x: 0,
                        y: 0,
                        anchorPointToId: `AnchorPoint-${holeAnchorPointOffset + 1}`,
                        horizontalCount: -1,
                        verticalCount: -1
                    }),
                    ...getRoundShapeAnchorPointRecipes('KickPlateCountersinkHoles-1', holeAnchorPointOffset)
                ]
            };
        }));
    }
    return recipes;
};

//# sourceMappingURL=hole.recipes.js.map